import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {catchError, map, pluck, startWith, switchMap, tap} from 'rxjs/operators';
import {AnalyticsService} from '../../shared/analytics.service';
import {ApiService, HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {NavigationService} from '../../shared/navigation.service';

@Component({
  selector: 'register-complete',
  templateUrl: './register-complete.component.html',
  styleUrls: ['./register-complete.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterCompleteComponent implements OnInit, OnDestroy {

  passwordCreation: FormGroup;
  usernameSub: Subscription;

  code$: Observable<string>;
  username$: Observable<string>;
  isUpdateButtonDisabled$: Observable<boolean>;
  isNewPasswordInvalid$: Observable<boolean>;
  isNewPasswordMissing$: Observable<boolean>;
  isConfirmPasswordInvalid$: Observable<boolean>;
  newPassword$: Observable<string>;
  newPasswordTouched$ = new BehaviorSubject(false);
  confirmPasswordTouched$ = new BehaviorSubject(false);
  loading$ = new BehaviorSubject(true);
  submitting$ = new BehaviorSubject(false);
  expired$ = new BehaviorSubject(false);

  constructor(private Api: ApiService,
              private Navigation: NavigationService,
              private Analytics: AnalyticsService,
              private Auth: AuthService,
              private formBuilder: FormBuilder,
              private route: ActivatedRoute) {
  }

  ngOnInit() {
    if (this.Auth.hasAuthentication()) {
      this.Navigation.goHome(true);
      return;
    }

    this.trackActivationCode();
    this.setupUsername();
    this.initializePasswordFormGroup();
    this.setupPasswordFormObservables();
    this.usernameSub = this.username$.subscribe();
  }

  get code(): string {
    return this.route.snapshot.queryParamMap.get('code');
  }

  trackActivationCode() {
    this.Analytics.trackEvent('Complete: Activation code submitted', {
      category: 'Action',
      code: this.code
    });
  }

  setupUsername() {
    this.code$ = this.route.queryParams.pipe(pluck('code'));
    this.username$ = this.code$
      .pipe(
        switchMap(code => this.Api.readUsernameFromCode(code)),
        tap(() => this.loading$.next(false)),
        catchError((err: HttpError) => {
          this.handleUsernameError(err);
          return of('');
        })
      );
  }

  initializePasswordFormGroup() {
    this.passwordCreation = this.formBuilder.group({
      newPassword: [''],
      confirmedPassword: ['']
    });
  }

  private setupPasswordFormObservables() {
    this.isUpdateButtonDisabled$ = this.passwordCreation.statusChanges
      .pipe(
        startWith(true),
        map(() => this.passwordCreation.invalid)
      );

    this.isNewPasswordMissing$ = combineLatest(
      this.passwordCreation.statusChanges,
      this.newPasswordTouched$,
      (statusChanges, newPasswordTouched) => {
        return this.passwordCreation.errors && this.passwordCreation.errors.newMissing && newPasswordTouched;
      });

    this.isNewPasswordInvalid$ = combineLatest(
      this.passwordCreation.statusChanges,
      this.newPasswordTouched$,
      (statusChanges, newPasswordTouched) => {
        return this.passwordCreation.errors && this.passwordCreation.errors.newInvalid && newPasswordTouched;
      });

    this.isConfirmPasswordInvalid$ = combineLatest(
      this.passwordCreation.statusChanges,
      this.confirmPasswordTouched$,
      (statusChanges, confirmPasswordTouched) => {
        return this.passwordCreation.errors && this.passwordCreation.errors.badConfirmation && confirmPasswordTouched;
      });

    this.newPassword$ = this.passwordCreation.valueChanges
      .pipe(
        startWith({newPassword: ''}),
        pluck('newPassword')
      );
  }

  ngOnDestroy() {
    if (this.usernameSub) this.usernameSub.unsubscribe();
  }

  handleUsernameError(err: HttpError) {
    if (err.code === 'invalid_code') {
      this.Analytics.trackEvent('Complete: Activation code invalid');
      this.Navigation.handleNotFound();
    } else if (err.code === 'expired_code') {
      this.Analytics.trackEvent('Complete: Activation code expired');
      this.expired$.next(true);
    } else if (err.code === 'already_registered') {
      this.Analytics.trackEvent('Complete: Already registered');
      this.Navigation.handleLogin(true, {registered: true});
    } else {
      this.Analytics.trackEvent('Complete: Account activation failed', {category: 'Error'});
      this.Navigation.handleError(err);
    }

    this.loading$.next(false);
  }

  createPassword() {
    this.submitting$.next(true);

    this.Api.completeRegistration(this.code, this.passwordCreation.get('newPassword').value).subscribe(data => {
      this.Auth.storeOAuthAuthentication(data);
      this.Api.refreshUser().subscribe(() => {
        this.submitting$.next(false);
        this.Analytics.trackEvent('Complete: Register password submitted success and account created');
        this.Navigation.goHome(true);
      });
    }, (err: HttpError) => {
      this.Analytics.trackEvent('Complete: Register password submitted failure', {registrationPasswordErrorCode: err.code});
      this.submitting$.next(false);
      this.Navigation.handleError(err);
    });
  }

}
